import React from "react"
import { useTranslation } from "react-i18next"

import type { User } from "@/models/user"
import { userRoles } from "@/models/user-role"
import { adminEntityFieldsMaxWidthSx } from "@/modules/admin/utils/admin-entity-fields-max-width-sx"
import { ReadonlyField } from "@/nzds/forms/readonly-field/readonly-field"
import { useAuthorizedOrganizations } from "@/services/organization/useAuthorizedOrganizations"
import { EMPTY_DATA_PLACEHOLDER } from "@/utils/constants"
import { getDisplayValue } from "@/utils/value"

import Chip from "@mui/material/Chip"
import Stack from "@mui/material/Stack"

interface UserReadonlyFieldsProps {
  user: User
}

export const UserReadonlyFields: React.FC<UserReadonlyFieldsProps> = ({
  user,
}) => {
  const { t } = useTranslation("common")
  const { authorizedOrganizationsData: authorizedOrganizations = [] } =
    useAuthorizedOrganizations()

  const role = userRoles.find((entry) => entry.id === user.role)
  const roleLabel = role ? t(role.name) : getDisplayValue(user.role)

  const organizationsById = new Map(
    authorizedOrganizations
      .filter((organization) => organization.id)
      .map((organization) => [String(organization.id), organization] as const)
  )

  const organizationIds = (user.organizationIds ?? []).map(String)

  const organizationsValue =
    organizationIds.length === 0 ? (
      EMPTY_DATA_PLACEHOLDER
    ) : (
      <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
        {organizationIds.map((organizationId) => (
          <Chip
            key={organizationId}
            label={
              organizationsById.get(organizationId)?.name ??
              t("editUser.organizationUnknownLabel")
            }
            variant="outlined"
          />
        ))}
      </Stack>
    )

  return (
    <Stack sx={{ ...adminEntityFieldsMaxWidthSx, gap: 2 }}>
      <ReadonlyField
        label={t("fields.firstName.title")}
        value={getDisplayValue(user.firstName)}
      />
      <ReadonlyField
        label={t("fields.lastName.title")}
        value={getDisplayValue(user.lastName)}
      />
      <ReadonlyField
        label={t("fields.email.title")}
        value={getDisplayValue(user.email)}
      />
      <ReadonlyField label={t("fields.role.title")} value={roleLabel} />
      <ReadonlyField
        label={t("editUser.organizationPickerLabel")}
        value={organizationsValue}
      />
    </Stack>
  )
}
